/**
 * Proving it is still you, without losing what you were doing.
 *
 * A few acts are held behind a recent re-authentication: granting the platform
 * role, setting somebody else's password, elevating into a clinic's data. The
 * refusal used to send the browser to the identity provider - which is right for
 * an SSO session and a dead end for every other one. With no SSO configured it
 * bounced to 「Googleログインは未設定です」, and with a password account there was
 * nothing to bounce to at all: the operator saw 「Re-authentication is required
 * for this action」 over a form they had just filled in, with no way to answer it.
 *
 * So the answer is asked for here, in place, and the request that triggered it is
 * retried on success - the form behind this dialog keeps its contents and the
 * save simply goes through. The SSO route stays as the fallback for an account
 * that holds no password.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, post, setStepUpHandler } from '../api.ts';
import { Modal } from './Modal.tsx';
import { Field } from './EditPanel.tsx';

export function StepUpDialog({ hasPassword }: { hasPassword: boolean }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [needCode, setNeedCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * The api layer hands us a promise to settle: true means "I re-authenticated,
   * retry the request", false means "give up and report the refusal". Kept in a
   * ref because it is resolved from an event handler, and a re-render must not
   * lose the caller waiting on it.
   */
  const decide = useRef<((ok: boolean) => void) | null>(null);

  useEffect(() => {
    setStepUpHandler(async () => {
      /*
       * An account with no password has nothing to type here, so it goes where
       * it always went: the provider, as a top-level navigation, with the way
       * back to this screen.
       */
      if (!hasPassword) {
        const back = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.assign(`/api/v1/auth/step-up?redirect_to=${back}`);
        return false;
      }
      setPassword('');
      setCode('');
      setNeedCode(false);
      setError(null);
      setOpen(true);
      return new Promise<boolean>((resolve) => {
        decide.current = resolve;
      });
    });
    return () => setStepUpHandler(null);
  }, [hasPassword]);

  function finish(ok: boolean) {
    setOpen(false);
    decide.current?.(ok);
    decide.current = null;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/auth/reauth', {
        password,
        ...(code.trim() ? { totp: code.trim() } : {}),
      });
      finish(true);
    } catch (err) {
      // The deployment may ask an operator for a mailed code as well; the server
      // says so rather than this form guessing.
      if (err instanceof ApiError && err.message.includes('code')) setNeedCode(true);
      setError('確認できませんでした。パスワードをご確認ください。');
    } finally {
      setBusy(false);
      setPassword('');
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => finish(false)}
      title="確認のため、もう一度パスワードを入力してください"
      caption="重要な操作の前だけお願いしています。入力中の内容はそのまま保持され、確認後に続きから保存されます。"
    >
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
      <form className="editpanel__body" onSubmit={(e) => void submit(e)}>
        <Field label="パスワード" htmlFor="su-password" required span="full">
          <input
            id="su-password"
            type="password"
            autoComplete="current-password"
            // The dialog exists to be answered immediately; anything else on
            // screen is the form it interrupted.
            autoFocus
            required
            maxLength={200}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        {needCode && (
          <Field label="確認コード" htmlFor="su-code" span="full" hint="メールに届いたコードです。">
            <input
              id="su-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={12}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
        )}

        <div className="editpanel__actions field--full">
          <button className="btn btn--sm" type="submit" disabled={busy || password === ''}>
            {busy ? '確認中…' : '確認して続ける'}
          </button>
          <button
            className="btn btn--ghost btn--sm"
            type="button"
            onClick={() => finish(false)}
          >
            キャンセル
          </button>
        </div>
      </form>
    </Modal>
  );
}
